import CopyOutlined from '@ant-design/icons/CopyOutlined';
import { Divider, Button as AntdButton } from 'antd';
import { memo, useContext, useState } from 'react';

import { PaymentDetailsFieldName, ResponsePaymentDetails, UserRequest, ResponseArtifact } from '@/api/types';

import api from '@/api';

import ModalContext from '../../../contexts/Modal/ModalContext';
import { saveFileFromBlob } from '../../../helpers/saveFileFromBlob';
import { Button } from '../../Button';
import { DownloadButton } from '../../Forms/Buttons';
import { ModalBorderTopColor, ModalConstructor } from '../ModalConstructor';

import './PaymentDetailsAlert.scss';

export interface PaymentDetailsAlertProps {
  detailsList: Omit<ResponsePaymentDetails, 'payData'>;
  currentRequestId: Nullable<UserRequest['id']>;
}

const PaymentDetailsFieldsMapping: Record<Exclude<PaymentDetailsFieldName, 'payData'>, Nullable<string>> = {
  [PaymentDetailsFieldName.price]: 'Сума (грн)',
  [PaymentDetailsFieldName.receiver]: 'Отримувач',
  [PaymentDetailsFieldName.edrpou]: 'ЄДРПОУ отримувача',
  [PaymentDetailsFieldName.iban]: 'Розрахунковий рахунок (IBAN)',
  [PaymentDetailsFieldName.purpose]: 'Призначення платежу',

  [PaymentDetailsFieldName.fee]: null,
  [PaymentDetailsFieldName.isPayable]: null,
};

const HEADER_TEXT = 'Реквізити для оплати';

export const PaymentDetailsAlert = memo(function PayOrLeaveModal({
  detailsList,
  currentRequestId,
}: PaymentDetailsAlertProps) {
  const { toggleModal } = useContext(ModalContext);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const downloadButtonAction = (): void => {
    setIsDownloading(true);
    currentRequestId &&
      api.services
        .getPaymentDetails<ResponseArtifact>(currentRequestId, true)
        .then(({ file, fileName }) => {
          saveFileFromBlob(fileName ?? '', file);
        })
        .finally(() => setIsDownloading(false));
  };

  return (
    <ModalConstructor modalBorderTopColor={ModalBorderTopColor.blue}>
      <div className="payment-details-modal">
        <div className="payment-details-modal__title">{HEADER_TEXT}</div>

        <Divider />

        <div className="details-list">
          {Object.entries(detailsList).map((details) => {
            const key = details[0] as Exclude<PaymentDetailsFieldName, 'payData'>;
            const label = PaymentDetailsFieldsMapping[key];
            const value = details[1];

            return (
              !!label && (
                <div key={details[0]}>
                  <div className="details-header">{label}</div>
                  <div className="details-text-container">
                    <div className="details-text">{value}</div>
                    <AntdButton
                      type="text"
                      aria-label="Копіювати"
                      color="primary"
                      shape="circle"
                      className="copyButton"
                      onClick={(): Promise<void> => navigator.clipboard.writeText(`${value}`)}
                      icon={<CopyOutlined />}
                    />
                  </div>
                </div>
              )
            );
          })}
        </div>

        <Divider />

        <div className="payment-details-modal__buttons-box">
          <DownloadButton
            outline
            text="Завантажити"
            onClick={downloadButtonAction}
            disabled={isDownloading}
            withStyles={false}
            downloading={isDownloading}
          />
          <Button
            onClick={(): void => {
              toggleModal(false);
            }}
          >
            Закрити
          </Button>
        </div>
      </div>
    </ModalConstructor>
  );
});
